import { createFileRoute } from "@tanstack/react-router";
import { Check, ChevronDown, SlidersHorizontal } from "lucide-react";
import { useEffect, useRef, useState } from "react";

import { CasesGrid } from "../components/cases/CasesGrid";
import { AutoScale } from "../components/site/AutoScale";
import { Breadcrumbs } from "../components/site/Breadcrumbs";
import { Footer } from "../components/site/Footer";
import { Header } from "../components/site/Header";
import { Reveal } from "../components/site/Reveal";
import { ScrollToTop } from "../components/site/ScrollToTop";
import { SpecialOffer } from "../components/home/SpecialOffer";
import { LeadForm } from "../components/shared/LeadForm";
import { buildMeta, breadcrumbJsonLd, SITE_URL } from "../lib/site";
import {
  industryLabels,
  industryOf,
  industryOrder,
  type Industry,
} from "../content/case-industries";
import { allCases } from "../content/casesAll";
import { hasCategory } from "../content/case-categories";

const title = "Кейсы HAY.agency — сайты, SEO, реклама и CRM";
const description =
  "Сайты, интернет-магазины и маркетинг — от запуска до постоянного развития.";
const metaDescription =
  "Проекты HAY.agency: разработка сайтов и интернет-магазинов, SEO, реклама, геосервисы, CRM и развитие digital-продуктов после запуска.";

const tabs = [
  { label: "Все проекты", items: allCases },
  { label: "Интернет-магазины", items: allCases.filter((c) => hasCategory(c.caseUrl, "ecom")) },
  { label: "Сайты", items: allCases.filter((c) => hasCategory(c.caseUrl, "site")) },
  { label: "Маркетинг", items: allCases.filter((c) => hasCategory(c.caseUrl, "marketing")) },
];

export const Route = createFileRoute("/cases/")({
  head: () => ({
    ...buildMeta({
      title,
      description: metaDescription,
      path: "/cases",
      image: `${SITE_URL}/og/cases.webp`,
      imageWidth: 1200,
      imageHeight: 630,
      imageAlt: "Кейсы HAY.agency",
      locale: "ru_RU",
    }),
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "CollectionPage",
              "@id": `${SITE_URL}/cases#page`,
              url: `${SITE_URL}/cases`,
              name: title,
              description: metaDescription,
              isPartOf: { "@id": `${SITE_URL}/#website` },
            },
            breadcrumbJsonLd(
              [
                ["Главная", "/"],
                ["Кейсы", "/cases"],
              ],
              { inGraph: true },
            ),
            {
              "@type": "ItemList",
              name: "Кейсы hay.agency",
              itemListElement: allCases.map((item, i) => ({
                "@type": "ListItem",
                position: i + 1,
                name: item.title,
              })),
            },
          ],
        }),
      },
    ],
  }),
  component: Page,
});

function Page() {
  const [active, setActive] = useState(0);
  const [industry, setIndustry] = useState<Industry | "all">("all");
  const [filterOpen, setFilterOpen] = useState(false);
  const filterRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!filterOpen) return;
    const onPointerDown = (event: PointerEvent) => {
      if (!filterRef.current?.contains(event.target as Node)) setFilterOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setFilterOpen(false);
    };
    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [filterOpen]);
  const current = tabs[active] ?? tabs[0]!;

  const byIndustry = current.items.filter(
    (item) => industry === "all" || industryOf(item.title) === industry,
  );
  const industryCounts = industryOrder
    .map((id) => ({
      id,
      label: industryLabels[id],
      count: current.items.filter((item) => industryOf(item.title) === id).length,
    }))
    .filter((entry) => entry.count > 0);

  const scrollToGrid = () =>
    document.getElementById("cases-grid")?.scrollIntoView({ behavior: "smooth", block: "start" });

  return (
    <AutoScale>
      <div className="min-h-screen bg-background font-sans text-foreground antialiased">
        <SpecialOffer />
        <Header />
        <main>
          <section className="mx-auto w-full px-5 pt-4 lg:px-[21px] lg:pt-[28px]">
            <Breadcrumbs current="Кейсы" />
            <Reveal priority className="mt-8 grid gap-8 lg:mt-[46px] lg:grid-cols-[minmax(0,1fr)_minmax(0,420px)] lg:items-end">
              <h1 className="section-title">Наши кейсы</h1>
              <p className="body-12 text-muted-foreground">{description}</p>
            </Reveal>

            <div className="relative mt-7 flex items-center gap-2 lg:mt-[38px]">
              <div className="no-scrollbar -mx-5 flex flex-1 gap-2 overflow-x-auto px-5 lg:mx-0 lg:flex-wrap lg:px-0">
                {tabs.map((tab, i) => (
                  <button
                    key={tab.label}
                    type="button"
                    onClick={() => {
                      setActive(i);
                      setIndustry("all");
                      scrollToGrid();
                    }}
                    aria-pressed={i === active}
                    className={`btn-label h-[44px] whitespace-nowrap px-6 transition-colors duration-300 lg:h-[48px] lg:px-[32px] ${
                      i === active
                        ? "bg-foreground text-background"
                        : "bg-secondary text-foreground hover:bg-brand hover:text-brand-foreground"
                    }`}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>

              <div ref={filterRef} className="relative shrink-0">
                <button
                  type="button"
                  onClick={() => setFilterOpen((v) => !v)}
                  aria-expanded={filterOpen}
                  aria-haspopup="listbox"
                  aria-label="Фильтр по отрасли"
                  className={`btn-label flex h-[44px] items-center gap-2 whitespace-nowrap px-5 transition-colors duration-300 lg:h-[48px] lg:px-[24px] ${
                    industry !== "all" || filterOpen
                      ? "bg-foreground text-background"
                      : "bg-secondary text-foreground hover:bg-brand hover:text-brand-foreground"
                  }`}
                >
                  <SlidersHorizontal className="size-[15px]" strokeWidth={2} />
                  <span className="hidden lg:inline">
                    {industry === "all" ? "Отрасль" : industryLabels[industry]}
                  </span>
                  <ChevronDown
                    className={`hidden size-[14px] transition-transform duration-300 lg:inline ${filterOpen ? "rotate-180" : ""}`}
                    strokeWidth={2}
                  />
                </button>

                <div
                  role="listbox"
                  aria-label="Отрасли"
                  className={`absolute right-0 top-[calc(100%+8px)] z-50 w-[280px] origin-top-right overflow-hidden border border-border bg-background shadow-[0_24px_60px_-24px_rgba(0,0,0,0.35)] transition-all duration-300 lg:w-[300px] ${
                    filterOpen
                      ? "pointer-events-auto translate-y-0 scale-100 opacity-100"
                      : "pointer-events-none -translate-y-2 scale-[0.98] opacity-0"
                  }`}
                >
                  <div className="max-h-[60vh] overflow-y-auto p-2">
                    <button
                      type="button"
                      role="option"
                      aria-selected={industry === "all"}
                      onClick={() => {
                        setIndustry("all");
                        setFilterOpen(false);
                        scrollToGrid();
                      }}
                      className={`group flex w-full items-center justify-between gap-3 px-3 py-2.5 text-left text-[14px] leading-[20px] transition-colors duration-200 ${
                        industry === "all"
                          ? "bg-foreground text-background"
                          : "text-foreground hover:bg-secondary"
                      }`}
                    >
                      <span className="flex items-center gap-2.5">
                        <Check
                          className={`size-[15px] transition-opacity duration-200 ${industry === "all" ? "opacity-100" : "opacity-0"}`}
                          strokeWidth={2.5}
                        />
                        Все отрасли
                      </span>
                      <span className={industry === "all" ? "text-[12px] opacity-70" : "text-[12px] text-muted-foreground"}>
                        {current.items.length}
                      </span>
                    </button>
                    {industryCounts.map((entry) => {
                      const selected = industry === entry.id;
                      return (
                        <button
                          key={entry.id}
                          type="button"
                          role="option"
                          aria-selected={selected}
                          onClick={() => {
                            setIndustry(entry.id);
                            setFilterOpen(false);
                            scrollToGrid();
                          }}
                          className={`group flex w-full items-center justify-between gap-3 px-3 py-2.5 text-left text-[14px] leading-[20px] transition-colors duration-200 ${
                            selected
                              ? "bg-foreground text-background"
                              : "text-foreground hover:bg-secondary"
                          }`}
                        >
                          <span className="flex items-center gap-2.5">
                            <Check
                              className={`size-[15px] transition-opacity duration-200 ${selected ? "opacity-100" : "opacity-0"}`}
                              strokeWidth={2.5}
                            />
                            {entry.label}
                          </span>
                          <span className={selected ? "text-[12px] opacity-70" : "text-[12px] text-muted-foreground"}>
                            {entry.count}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>

              <span className="ml-auto hidden items-center whitespace-nowrap text-[13px] leading-[18px] text-muted-foreground lg:flex">
                {byIndustry.length} проектов
              </span>
            </div>
          </section>

          <section
            id="cases-grid"
            className="mx-auto w-full scroll-mt-[120px] px-5 pb-10 pt-6 lg:px-[21px] lg:pb-[70px] lg:pt-[38px]"
          >
            <CasesGrid key={`${active}-${industry}`} items={byIndustry} />
          </section>

          <LeadForm
            title={
              <>
                Давайте обсудим, как мы можем масштабировать
                <br className="hidden lg:block" /> ваш бизнес
              </>
            }
            lead="Оставьте заявку — свяжемся в течение двух часов, чтобы задать вопросы и обсудить задачу."
            submitLabel="Обсудить проект"
          />
        </main>
        <Footer />
        <ScrollToTop />
      </div>
    </AutoScale>
  );
}
